import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Cookies from "universal-cookie";
import { MoreHorizontal, Settings, Power, X } from "react-feather";
import { mobileTabsFor } from "../constants/SideMenu";
import { setUserInfo, setUserToken } from "../redux/authSlice";
import xavaLogo from "../assets/images/xava-logo.png";
import userAvatar from "../assets/images/avatar-s-11.jpg";
import "./mobileNav.scss";

const cookies = new Cookies();

// Title shown in the app bar — the route's own name, so you always know
// where you are without a breadcrumb.
const TITLES = {
  "/dashboard": "Dashboard",
  "/client-list": "Clients",
  "/client-form": "Add client",
  "/clientDetail": "Client",
  "/user-list": "Staff",
  "/user-form": "Add staff",
  "/employeemanagement": "Employees",
  "/employeedetails": "Employee",
  "/employeesalary": "Salary",
  "/contractormanagement": "Contractors",
  "/contractordetails": "Profile",
  "/contractorform": "Contractor",
  "/contractorsalary": "Contractor salary",
  "/contractorinvoice": "Invoices",
  "/requests": "Approvals",
  "/tickets": "Tickets",
  "/manage-holidays": "Holidays",
  "/tasklist": "Tasks",
  "/profile": "Company profile",
  "/accountsettings": "Settings",
  "/pending-list": "Pending",
  "/salaryStatus": "Salary status",
  "/EmpAttendance": "Attendance",
  "/createinvoice": "New invoice",
};

function MobileNav({ badgeFor }) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state?.authState?.userInfo);
  const [sheetOpen, setSheetOpen] = useState(false);

  const canSee = (item) => {
    if (!item?.permission) return true;
    if (["admin", "superAdmin", "Super Admin"].includes(userInfo?.role)) return true;
    const perms = userInfo?.permissions || [];
    return perms.includes(item.permission) || perms.includes("fullAccess");
  };
  const resolved = mobileTabsFor(userInfo?.role);
  const tabs = resolved.tabs.filter(canSee);
  const rest = resolved.rest.filter(canSee);

  // Never leave the sheet hanging over a new page
  useEffect(() => {
    setSheetOpen(false);
  }, [location.pathname]);

  // The sheet is a modal surface: stop the page behind it from scrolling
  useEffect(() => {
    document.body.classList.toggle("xv-sheet-locked", sheetOpen);
    return () => document.body.classList.remove("xv-sheet-locked");
  }, [sheetOpen]);

  const title =
    TITLES[location.pathname] ||
    TITLES[Object.keys(TITLES).find((k) => location.pathname.startsWith(k))] ||
    "Xava CRM";

  const isActive = (to) => location.pathname.toLowerCase() === to.toLowerCase();
  const restActive = rest.some((r) => isActive(r.to));

  const logout = () => {
    localStorage.clear();
    cookies.remove("MMCL", { path: "/" });
    cookies.remove("MMCL_user", { path: "/" });
    cookies.remove("isLoggedIn", { path: "/" });
    dispatch(setUserInfo({}));
    dispatch(setUserToken(""));
    navigate("/");
  };

  return (
    <>
      <header className="xv-mtopbar">
        <img src={xavaLogo} alt="Xava" className="xv-mtopbar-logo" />
        <h1 className="xv-mtopbar-title">{title}</h1>
        <button
          type="button"
          className="xv-mtopbar-avatar"
          onClick={() => setSheetOpen(true)}
          aria-label="Account and more"
        >
          <img src={userInfo?.profilePicUrl || userAvatar} alt="" />
        </button>
      </header>

      <nav className="xv-mtabs" aria-label="Main">
        {tabs.map((t) => {
          const badge = badgeFor?.(t.to);
          return (
            <Link
              key={t.to}
              to={t.to}
              className={`xv-mtab ${isActive(t.to) ? "is-active" : ""}`}
            >
              <span className="xv-mtab-icon">
                {t.icon}
                {badge ? <span className="xv-mtab-badge">{badge}</span> : null}
              </span>
              <span className="xv-mtab-label">{t.label}</span>
            </Link>
          );
        })}

        {rest.length > 0 && (
          <button
            type="button"
            className={`xv-mtab ${sheetOpen || restActive ? "is-active" : ""}`}
            onClick={() => setSheetOpen(true)}
          >
            <span className="xv-mtab-icon">
              <MoreHorizontal />
              {rest.some((r) => badgeFor?.(r.to)) ? <span className="xv-mtab-dot" /> : null}
            </span>
            <span className="xv-mtab-label">More</span>
          </button>
        )}
      </nav>

      {sheetOpen && (
        <div
          className="xv-sheet-backdrop"
          onClick={() => setSheetOpen(false)}
          role="presentation"
        />
      )}

      <div
        className={`xv-sheet ${sheetOpen ? "is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-hidden={!sheetOpen}
      >
        <div className="xv-sheet-grab" />

        <div className="xv-sheet-head">
          <div className="xv-sheet-user">
            <img src={userInfo?.profilePicUrl || userAvatar} alt="" />
            <div>
              <span className="xv-sheet-name text-capitalize">
                {userInfo?.fname} {userInfo?.lname}
              </span>
              <span className="xv-sheet-role text-capitalize">{userInfo?.role}</span>
            </div>
          </div>
          <button
            type="button"
            className="xv-sheet-close"
            onClick={() => setSheetOpen(false)}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="xv-sheet-grid">
          {rest.map((r) => {
            const badge = badgeFor?.(r.to);
            return (
              <Link
                key={r.to}
                to={r.to}
                className={`xv-sheet-item ${isActive(r.to) ? "is-active" : ""}`}
              >
                <span className="xv-sheet-item-icon">
                  {r.icon}
                  {badge ? <span className="xv-mtab-badge">{badge}</span> : null}
                </span>
                <span>{r.label}</span>
              </Link>
            );
          })}
          <Link to="/accountsettings" className="xv-sheet-item">
            <span className="xv-sheet-item-icon">
              <Settings />
            </span>
            <span>Settings</span>
          </Link>
        </div>

        <button type="button" className="xv-sheet-logout" onClick={logout}>
          <Power size={16} /> Log out
        </button>
      </div>
    </>
  );
}

export default MobileNav;
